import { useState, useEffect } from 'react';
import { Text } from '@artprov/design-system';
import { getMatchingBancarioDocumento } from '../../../services/matchingBancario.service';
import { errorService } from '../../../common/utils/functions/error.service';
import PropTypes from 'prop-types';

const DocumentoCard = ({ embargoId, documentoMetadata, fields = [] }) => {
    const [pdfUrl, setPdfUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);

    useEffect(() => {
        if (!documentoMetadata?.disponible) return;

        const fetchPdf = async () => {
            setLoading(true);
            setError(false);

            try {
                const blob = await getMatchingBancarioDocumento(embargoId);
                const url = URL.createObjectURL(blob);
                setPdfUrl(url);

                // Cleanup
                return () => {
                    URL.revokeObjectURL(url);
                };
            } catch (err) {
                errorService.handleError(err, {
                    userMessage: "No pudimos cargar el documento.",
                    origen: "DocumentoCard.fetchPdf",
                    shouldToast: false,
                });
                setError(true);
            } finally {
                setLoading(false);
            }
        };

        fetchPdf();
    }, [embargoId, documentoMetadata]);

    if (!documentoMetadata?.disponible) {
        return (
            <div className="bg-tertiary-100 border border-tertiary-95 rounded py-6 px-4">
                <Text variant="subtitle1" className="text-primary-10">
                    Documento Email Embargo
                </Text>

                <div className="h-px bg-tertiary-80 my-4" />

                {/* Campos en columna única */}
                <div className="flex flex-col gap-4">
                    {fields.map((field) => (
                        <div key={field.label} className="flex flex-col gap-1">
                            {/* Etiqueta */}
                            <Text variant="body2" className="text-tertiary-60">
                                {field.label}
                            </Text>

                            {/* Valor */}
                            <Text variant="body2" className="text-tertiary-10 font-semibold">
                                {field.value || "-"}
                            </Text>
                        </div>
                    ))}
                </div>

                <div className="mt-4 border border-dashed border-tertiary-80 rounded p-8 flex items-center justify-center">
                    <Text variant="body2" className="text-tertiary-70">
                        No hay documento disponible
                    </Text>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-tertiary-100 border border-tertiary-95 rounded py-6 px-4 min-h-[1180px]">
            <Text variant="subtitle1" className="text-primary-10">
                Documento Email Embargo
            </Text>

            <div className="h-px bg-tertiary-80 my-4" />

            {/* Campos en columna única */}
            <div className="flex flex-col gap-4">
                {fields.map((field) => (
                    <div key={field.label} className="flex flex-col gap-1">
                        {/* Etiqueta */}
                        <Text variant="body2" className="text-tertiary-60">
                            {field.label}
                        </Text>

                        {/* Valor */}
                        <Text variant="body2" className="text-tertiary-10 font-semibold">
                            {field.value || "-"}
                        </Text>
                    </div>
                ))}
            </div>

            {/* Preview del documento */}
            {loading && (
                <div className="mt-4 border border-tertiary-90 rounded p-8 flex items-center justify-center">
                    <Text variant="body2" className="text-tertiary-70">
                        Cargando documento...
                    </Text>
                </div>
            )}

            {pdfUrl && !loading && !error && (
                <embed
                    src={pdfUrl}
                    type="application/pdf"
                    className="mt-4 w-full h-[1000px]"
                />
            )}

            {error && (
                <div className="mt-4 border border-tertiary-90 rounded p-8 flex items-center justify-center">
                    <Text variant="body2" className="text-error-50">
                        Error al cargar el documento
                    </Text>
                </div>
            )}

        </div>
    );
};

DocumentoCard.propTypes = {
    embargoId: PropTypes.string.isRequired,
    documentoMetadata: PropTypes.shape({
        disponible: PropTypes.bool.isRequired,
    }).isRequired,
    fields: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            value: PropTypes.string,
        })
    ),
};

export default DocumentoCard;